/**
 * W5c — the fixture state for the system-maintenance doors (2026-11-04).
 *
 * This file is the DOORS' state, not an invented deployment: demo rows, the refusal
 * sentences copied VERBATIM from the server (`acs_service/problems.py` and
 * `acs_service/retention.py` — a fixture that paraphrased them would be a console word
 * standing where the door's word stands), and the verify-verdict mapping that keeps the
 * three-state `intact` honest. The four migration refusals and `recorded: null` are NOT
 * default states here — the page tests inject them (`apiWith`), because a fixture that
 * posed as an unrecorded store by default would be claiming a fact about nothing.
 *
 * Named `sysops` because `maintenance.ts` is AL-05's maintenance-WINDOWS family — a
 * different family with its own store; the two must never share a file.
 */
import type {
  BackupChecks,
  BackupEntry,
  BackupVerdict,
  LegalHold,
  LegalHoldScope,
  ListOutboxQuery,
  MigrationsApplyAccepted,
  MigrationApplied,
  MigrationStatus,
  OutboxListResponse,
  OutboxRow,
  RetentionRunAccepted,
} from '../api/types';

/* ---- the server's own sentences ---- */

export function eventNotFound(): string {
  return 'no outbox delivery record with this event id';
}

export function eventNotRedrivable(): string {
  return 'the row is not a dead letter, so there is no spent budget to reset';
}

export function migrationUnrecorded(): string {
  return 'this store has no recorded schema version — --baseline first';
}

export function migrationAhead(): string {
  return "the store's schema version is ahead of this build";
}

export function migrationBusy(): string {
  return 'another migrator holds the migrations pass';
}

export function migrationLostRace(): string {
  return 'another migrator advanced the schema record while this one was running';
}

export function backupNotFound(): string {
  return 'no backup artifact of that name in the configured backup directory';
}

export function backupDirUnavailable(): string {
  return 'the configured backup directory is not a readable directory';
}

export function legalHoldNotFound(): string {
  return 'no legal hold with this id is known to the ACS';
}

export function legalHoldAlreadyLifted(): string {
  return 'this legal hold is already lifted; its lifting stands and is not repeated';
}

export function retentionHeldSentence(): string {
  return "legal hold active for family 'informs': nothing was deleted";
}

/** `retention.py`'s outbox sibling — the schema exposes it as `outbox_pruned: 0` only. */
export function retentionOutboxHeldSentence(): string {
  return "legal hold active for family 'outbox': nothing was deleted";
}

/* ---- demo state ---- */

export const FIXTURE_OUTBOX_ROWS: OutboxRow[] = [
  {
    event_id: 'evt:1f0a91c2',
    status: 'failed',
    attempts: 12,
    redrives: 0,
    created_at: '2026-11-04T05:12:04Z',
    sent_at: null,
    next_attempt_at: null,
    last_error: 'webhook-http-error: ConnectError',
    event: { type: 'com.ladtelecom.acs.job.verdict', subject: 'dev:u08f-1' },
  },
  {
    event_id: 'evt:2b77c0d1',
    status: 'sent',
    attempts: 2,
    redrives: 0,
    created_at: '2026-11-04T05:09:44Z',
    sent_at: '2026-11-04T05:09:47Z',
    next_attempt_at: null,
    last_error: null,
    event: { type: 'com.ladtelecom.acs.device.inform', subject: 'dev:u08f-2' },
  },
  {
    event_id: 'evt:3c11aa9e',
    status: 'failed',
    attempts: 6,
    redrives: 1,
    created_at: '2026-11-04T04:41:10Z',
    sent_at: null,
    next_attempt_at: null,
    last_error: 'webhook-429: rate limited by consumer',
    event: { type: 'com.ladtelecom.acs.alarm.raised', subject: 'dev:u08f-3' },
  },
  {
    event_id: 'evt:4d02bb87',
    status: 'pending',
    attempts: 1,
    redrives: 0,
    created_at: '2026-11-04T06:02:31Z',
    sent_at: null,
    next_attempt_at: '2026-11-04T06:12:31Z',
    last_error: null,
    event: { type: 'com.ladtelecom.acs.device.completed', subject: 'dev:u08f-4' },
  },
  {
    event_id: 'evt:5e99ff03',
    status: 'sent',
    attempts: 1,
    redrives: 0,
    created_at: '2026-11-04T06:00:02Z',
    sent_at: '2026-11-04T06:00:03Z',
    next_attempt_at: null,
    last_error: null,
    event: { type: 'com.ladtelecom.acs.job.verdict', subject: 'dev:u08f-5' },
  },
];

export const FIXTURE_BACKUPS: BackupEntry[] = [
  {
    id: 'acs-20261103T030000Z.json',
    kind: 'dump',
    size_bytes: 8_123_456,
    modified_at: '2026-11-03T03:00:12Z',
    schema_version: 7,
    backend: 'mongo',
    has_integrity: true,
  },
  {
    id: 'acs-20261102T030000Z',
    kind: 'mongodump',
    size_bytes: 245_119_104,
    modified_at: '2026-11-02T03:00:41Z',
    schema_version: null,
    backend: 'mongo',
    has_integrity: false,
  },
];

export const FIXTURE_MIGRATION_HISTORY: MigrationApplied[] = [
  {
    version: 5,
    name: 'n6-campaign-notify-capability-keys',
    at: '2026-11-03T02:00:11Z',
    result: '3 client rows re-scoped',
  },
  {
    version: 6,
    name: 'u17-legal-hold-meta',
    at: '2026-11-03T02:00:11Z',
    result: 'acs_meta document created',
  },
  {
    version: 7,
    name: 'w1-reports-config-capability-keys',
    at: '2026-11-03T02:00:12Z',
    result: '2 capability keys seeded',
  },
];

/** The demo informs family's aged rows — the demo count the held/clean distinction needs. */
export const FIXTURE_INFORMS_AGED = 1280;

/* ---- the store ---- */

export class SysOpsStore {
  private outbox: OutboxRow[];

  private holds: LegalHold[] = [];

  private holdSeq = 0;

  constructor() {
    this.outbox = FIXTURE_OUTBOX_ROWS.map((row) => ({ ...row }));
  }

  listOutbox(query: ListOutboxQuery): OutboxListResponse {
    const status = query.status;
    const rows = this.outbox.filter((row) => (status ? row.status === status : true));
    const limit = query.limit ?? 50;
    const offset = query.cursor ? Number(query.cursor) : 0;
    const page = rows.slice(offset, offset + limit);
    const next = offset + limit < rows.length ? String(offset + limit) : null;
    return { items: page, next_cursor: next };
  }

  outboxRow(eventId: string): OutboxRow | undefined {
    const row = this.outbox.find((entry) => entry.event_id === eventId);
    return row ? { ...row } : undefined;
  }

  /** The budget reset, not the memory wipe: `last_error` survives on purpose. */
  markRedriven(eventId: string): OutboxRow {
    const row = this.outbox.find((entry) => entry.event_id === eventId);
    if (!row) throw new Error(eventNotFound());
    row.status = 'pending';
    row.attempts = 0;
    row.redrives = (row.redrives ?? 0) + 1;
    row.next_attempt_at = null;
    return { ...row };
  }

  migrationStatus(): MigrationStatus {
    return {
      recorded: 7,
      target: 7,
      applied: FIXTURE_MIGRATION_HISTORY.map((step) => ({ ...step })),
      pending: [],
      detail: 'the demo store stands at the build’s own target',
    };
  }

  applyMigrations(): MigrationsApplyAccepted {
    // `applied: []` is the record saying everything already ran — never a silent no-op.
    return {
      recorded: 7,
      target: 7,
      applied: [],
      pending: [],
      detail: 'the store’s record already names this build’s target',
    };
  }

  backups(): BackupEntry[] {
    return FIXTURE_BACKUPS.map((entry) => ({ ...entry }));
  }

  backup(backupId: string): BackupEntry | undefined {
    return FIXTURE_BACKUPS.find((entry) => entry.id === backupId);
  }

  legalHolds(): LegalHold[] {
    return this.holds.map((hold) => ({ ...hold }));
  }

  hasActiveHold(scope: LegalHoldScope): boolean {
    return this.holds.some((hold) => hold.status === 'active' && covers(hold.scope, scope));
  }

  placeHold(scope: LegalHoldScope, reason: string, actor: string, at: string): LegalHold {
    this.holdSeq += 1;
    const hold: LegalHold = {
      hold_id: `hold:${actor}:${this.holdSeq}`,
      scope,
      reason,
      placed_by: actor,
      placed_at: at,
      status: 'active',
      lifted_by: null,
      lifted_at: null,
      lift_reason: null,
    };
    this.holds.push(hold);
    return { ...hold };
  }

  markLifted(holdId: string, reason: string, actor: string, at: string): LegalHold {
    const hold = this.holds.find((entry) => entry.hold_id === holdId);
    if (!hold) throw new Error(legalHoldNotFound());
    if (hold.status !== 'active') throw new Error(legalHoldAlreadyLifted());
    hold.status = 'lifted';
    hold.lifted_by = actor;
    hold.lifted_at = at;
    hold.lift_reason = reason;
    return { ...hold };
  }

  /**
   * The retention pass over the demo informs family — the U-17 gate verbatim: a held
   * family reports `*_held: true` and a `skipped` sentence, NEVER a bare zero.
   */
  runRetentionCounters(days: number): RetentionRunAccepted {
    const held = this.hasActiveHold('informs');
    const outboxHeld = this.hasActiveHold('outbox');
    return {
      informs_purged: held ? 0 : FIXTURE_INFORMS_AGED,
      retention_days: days,
      informs_cutoff: held ? null : '2026-10-05T06:00:00Z',
      skipped: held ? retentionHeldSentence() : null,
      informs_held: held,
      outbox_pruned: 0,
      outbox_cutoff: outboxHeld ? null : '2026-11-04T06:00:00Z',
      outbox_retention_days: 0,
    };
  }
}

function covers(holdScope: LegalHoldScope, family: LegalHoldScope): boolean {
  return holdScope === 'all' || holdScope === family;
}

/* ---- the verify verdict, from the artifact's own record ---- */

/** Files the demo manifest names — the number `intact`'s sentence quotes. */
const FIXTURE_DUMP_FILES_CHECKED = 14;

/**
 * The three-state verdict mapping (`routers/admin.py::verify_backup`): `has_integrity: true`
 * checks out, `false` means there was NO record and the question stays unanswered — a fixture
 * that answered `true` for a manifest-less mongodump would tell the one lie this door exists
 * to refuse.
 */
export function backupVerdictOf(entry: BackupEntry, actor: string): BackupVerdict {
  const checks: BackupChecks =
    entry.has_integrity === true
      ? { files_checked: FIXTURE_DUMP_FILES_CHECKED, mismatches: [] }
      : {};
  const reason =
    entry.has_integrity === true ? null : 'the artifact carries no integrity record to check';
  const state =
    entry.has_integrity === true
      ? `${entry.id} is intact (${FIXTURE_DUMP_FILES_CHECKED} file(s) hashed)`
      : `${entry.id} could not be checked: ${reason}`;
  const restore =
    entry.schema_version === null
      ? 'the artifact names no schema version, so whether a restore leaves a pending step is unknown until someone reads it'
      : `a restore lands at version ${entry.schema_version}, which this build reads with nothing pending`;
  return {
    id: entry.id,
    kind: entry.kind,
    intact: entry.has_integrity === true ? true : null,
    reason,
    schema_version: entry.schema_version ?? null,
    size_bytes: entry.size_bytes,
    modified_at: entry.modified_at,
    checks,
    detail: `${state}; ${restore} — verified by ${actor}`,
  };
}
